import { useTheme } from "@/theme/ThemeProvider";
import { getShadowStyle } from "@/theme/shadowStyle";
import { KPI_TILE_MAX_HEIGHT } from "@/ui/board/kpiBarLayout";
import type { IconComponent } from "@/ui/entityVisuals";
import { Pressable, Text, View, type ViewStyle } from "react-native";

function countValue(value: string | number): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const trimmed = value.trim().replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  return Number(trimmed);
}

export type SummaryTileTone =
  | "primary"
  | "warning"
  | "danger"
  | "neutral"
  | "info";

/**
 * Prägnante Summary-Kachel: große Zahl, ruhiges Meta-Label, Ton-Akzent.
 */
export function SummaryTile({
  label,
  value,
  tone = "neutral",
  icon: Icon,
  onPress,
  selected = false,
  dense = false,
  style,
}: {
  label: string;
  value: string | number;
  tone?: SummaryTileTone;
  icon?: IconComponent;
  onPress?: () => void;
  /** Gesetzter KPI-Filter. Der Rahmen wird primary, der Ton bleibt wertabhängig. */
  selected?: boolean;
  /** 44-px-Leiste: Zahl und Label in einer Zeile. */
  dense?: boolean;
  style?: ViewStyle;
}) {
  const { theme } = useTheme();
  const numeric = countValue(value);
  const activeTone: SummaryTileTone =
    numeric == null ? tone : numeric > 0 ? tone : "neutral";

  const tones: Record<
    SummaryTileTone,
    { bg: string; fg: string; bar: string }
  > = {
    primary: {
      bg: theme.color.accentBg,
      fg: theme.color.primary,
      bar: theme.color.primary,
    },
    warning: {
      bg: theme.color.warningBg,
      fg: theme.color.warningText,
      bar: theme.color.warning,
    },
    danger: {
      bg: theme.color.dangerBg,
      fg: theme.color.dangerText,
      bar: theme.color.danger,
    },
    info: {
      bg: theme.color.infoBg,
      fg: theme.color.info,
      bar: theme.color.info,
    },
    neutral: {
      bg: theme.color.badgeMutedBg,
      fg: theme.color.text,
      bar: theme.color.borderStrong,
    },
  };

  const t = tones[activeTone];
  const shadow = dense ? undefined : getShadowStyle(theme.cardShadow);
  // Dichte Kachel: Höhe fest. flexBasis/flexGrow gehören nur auf das
  // Zeilen-Element (Pressable), sonst wird flexBasis auf dem Web zur Höhe.
  const denseLock: ViewStyle = {
    alignSelf: "flex-start",
    height: theme.table.rowTouch,
    minHeight: theme.table.rowTouch,
    maxHeight: KPI_TILE_MAX_HEIGHT,
  };
  const frame: ViewStyle = dense
    ? {
        ...denseLock,
        width: "100%",
        justifyContent: "center",
        paddingVertical: 0,
        paddingHorizontal: theme.space.xs,
        paddingLeft: theme.space.xs + 4,
      }
    : {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 140,
        minWidth: 0,
        maxWidth: "100%",
        paddingVertical: theme.space.sm,
        paddingHorizontal: theme.space.md,
        paddingLeft: theme.space.md + 4,
      };
  const rowItem: ViewStyle = dense
    ? {
        ...denseLock,
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minWidth: 0,
        maxWidth: "100%",
      }
    : {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 140,
        minWidth: 0,
        maxWidth: "100%",
      };

  const body = (
    <View
      style={[
        {
          backgroundColor: theme.color.surface,
          borderRadius: theme.radius.md,
          borderWidth: selected ? 2 : 1,
          borderColor: selected
            ? theme.color.primary
            : activeTone === "neutral"
              ? theme.color.border
              : t.bar,
          overflow: "hidden",
          ...shadow,
          ...frame,
        },
        onPress ? null : style,
      ]}
    >
      <View
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          backgroundColor: t.bar,
        }}
      />
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: dense ? "flex-start" : "space-between",
          gap: dense ? theme.space.xs : theme.space.sm,
          minWidth: 0,
        }}
      >
        <Text
          style={{
            color: dense && activeTone !== "neutral" ? t.fg : theme.color.text,
            fontSize: dense ? theme.fontSize.body : 26,
            fontWeight: "800",
            letterSpacing: dense ? 0 : -0.6,
            lineHeight: dense ? 18 : 30,
            fontVariant: ["tabular-nums"],
          }}
        >
          {value}
        </Text>
        {dense ? (
          <Text
            style={{
              flex: 1,
              minWidth: 0,
              color: theme.color.text,
              fontSize: theme.fontSize.small,
              fontWeight: "700",
            }}
            numberOfLines={1}
          >
            {label}
          </Text>
        ) : null}
        {!dense && Icon ? (
          <View
            style={{
              width: 28,
              height: 28,
              borderRadius: theme.radius.sm,
              backgroundColor: t.bg,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={16} color={t.fg} weight="duotone" />
          </View>
        ) : null}
      </View>
      {dense ? null : (
        <Text
          style={{
            marginTop: 4,
            color: theme.color.muted,
            fontSize: theme.fontSize.tiny,
            fontWeight: "700",
            letterSpacing: 0.35,
            textTransform: "uppercase",
          }}
          numberOfLines={2}
        >
          {label}
        </Text>
      )}
    </View>
  );

  if (!onPress) return body;
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${value}`}
      {...(selected
        ? { "aria-pressed": true as const }
        : { "aria-pressed": false as const })}
      style={[rowItem, style]}
    >
      {body}
    </Pressable>
  );
}
